import React, { useState, useCallback, useMemo } from 'react';
import { Settings } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Language } from '@/components/page-content-v4/types';
import languagesData from '@/utils/data/language.json';

interface LanguageSelectorProps {
  selectedLanguage: string;
  locales: Language[];
  defaultLanguage: string;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ selectedLanguage, locales, defaultLanguage }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();

  const handleLanguageChange = useCallback((code: string) => {
    setSearchParams(`cl=${code}`);
    setDropdownOpen(false);
  }, [setSearchParams]);

  // Combine default language with locales, ensuring no duplicates
  const allLanguages = useMemo(() => {
    const defaultLang = languagesData.find(lang => lang.value === defaultLanguage);
    if (!defaultLang) return locales;

    // Convert default language to the same format as locales
    const defaultLocale = {
      code: defaultLang.value,
      label: defaultLang.name,
      icon: defaultLang.icon
    };

    // Filter out the default language from locales if it exists
    const filteredLocales = locales.filter(locale => locale.code !== defaultLanguage);
    return [defaultLocale, ...filteredLocales];
  }, [defaultLanguage, locales]);

  return (
    <div className="relative">
      <button
        className="flex items-center px-3 py-2 border border-gray-200 rounded-md bg-white hover:bg-gray-50 text-gray-700  text-sm"
        onClick={() => setDropdownOpen(!dropdownOpen)}
      >
        {languagesData.find(lang => lang.value === selectedLanguage)?.icon}
        <span className="ml-2">{languagesData.find(lang => lang.value === selectedLanguage)?.name}</span>
      </button>

      {dropdownOpen && (
        <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-md z-50">
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-medium text-gray-900">Languages</h3>
          </div>
          <div className="py-2">
            {allLanguages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 ${
                  selectedLanguage === lang.code ? 'bg-gray-50' : ''
                }`}
              >
                {lang.icon}
                <span className="ml-2">{lang.label}</span>
              </button>
            ))}
          </div>
          <div className="border-t border-gray-100">
            <button 
              onClick={() => navigate('/settings/general')}
              className="flex items-center w-full px-4 py-3 text-sm text-gray-600 hover:bg-gray-50"
            >
              <Settings size={16} className="text-gray-400" />
              <span className="ml-2">Manage Languages</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
